import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Creative, DisplayCreative } from '@feathr/blackbox';
import { FormSummaryItem, TanStackTable } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import type { ISummaryProps } from './CampaignSummary.types';
import creativesSummaryColumns from './creativesSummaryColumns';

const CreativesSummary = observer(
  ({ campaign, validationErrors, creatives: localCreatives }: ISummaryProps): JSX.Element => {
    const { t } = useTranslation();
    const { Creatives } = useStore();
    const columns = useMemo(() => creativesSummaryColumns(t), [t]);

    // If local creatives are provided (from review step), use those (for displaying ephemeral creatives)
    // Otherwise, get creatives from API (for campaign summaries/reports)
    const apiCreatives = localCreatives
      ? null
      : (Creatives.list({
          filters: {
            _parent: campaign.id,
            is_archived__ne: true,
          },
          pagination: {
            page_size: 1000,
          },
        }) as ListResponse<DisplayCreative>);

    // Use local creatives if available (includes ephemeral), otherwise use API creatives
    const creativesToShow = localCreatives ?? apiCreatives?.models ?? [];
    const isPending = localCreatives ? false : apiCreatives?.isPending;

    const filteredCreatives = creativesToShow.filter((c) => !c.get('is_archived'));
    const hasCreatives = filteredCreatives.length > 0;

    return (
      <FormSummaryItem
        errors={validationErrors?.redirects}
        label={t('Creatives')}
        name={'summary_creatives'}
        value={
          !hasCreatives && !isPending ? (
            t('(None)')
          ) : (
            <TanStackTable<Creative>
              columns={columns}
              idKey={'id'}
              isLoading={isPending}
              isPaginated={false}
              items={filteredCreatives}
              noDataText={t('No creatives')}
            />
          )
        }
      />
    );
  },
);

CreativesSummary.displayName = 'CreativesSummary';

export default CreativesSummary;
